import React, { useEffect, useRef } from 'react';

import { Button, Icon } from '@components/index';

import { StatusDropdownProps, StatusOption } from './types';

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  statusOptions,
  isDropdownOpen,
  stepStatusLabel,
  onToggleDropdown,
  onStatusChange,
  disabled = false
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isDropdownOpen) {
          onToggleDropdown();
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, onToggleDropdown]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        handleClick={onToggleDropdown}
        disabled={disabled}
        className="flex h-8 min-w-fit items-center justify-between rounded-3xl !bg-[#E8FFF4] px-3 py-1"
        variant="transparent"
        size="none"
      >
        <div className="flex items-center gap-2">
          <div className="flex size-4 items-center justify-center">
            {disabled ? (
              <div className="border-t-blue-600 size-3 animate-spin rounded-full border-2 border-gray-300" />
            ) : (
              <Icon name="check-circle" className="size-4 text-green-600" />
            )}
          </div>
          <span className="whitespace-nowrap text-sm font-medium text-gray-900">
            {stepStatusLabel}
          </span>
        </div>
        <div className="ml-1 flex size-4 items-center justify-center">
          <Icon
            name="down"
            className={`size-4 text-gray-900 transition-transform duration-200 ${
              isDropdownOpen ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </div>
      </Button>

      {isDropdownOpen && statusOptions.length > 0 && (
        <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-2xl border border-gray-100 bg-white p-2 shadow-lg">
          {statusOptions.map((option: StatusOption) => (
            <Button
              key={option.id}
              handleClick={() => onStatusChange(option.id)}
              className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-left transition-colors duration-150 hover:bg-gray-50"
              variant="transparent"
              size="none"
            >
              <span className="text-base font-medium text-gray-900">
                {option.label}
              </span>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusDropdown;
